// RecordPOC.js — Documentation Fonctionnelle : POC « Enregistrement (REC) » (module Environnement).
// Aperçu interactif de l'écran réel d'enregistrement de tâches, fidèle à sa DISPOSITION :
//  - en-tête dégradé (titre + fil d'Ariane + boutons Démarrer / Arrêter / Sauvegarder + état connexion) ;
//  - panneau GAUCHE (w-80) : arborescence Environnement › Fonction (en haut), carte de la fonction
//    sélectionnée, puis formulaire « Informations de la tâche » EN BAS ;
//  - panneau CENTRAL : zone de capture de l'application cible (vue agent) ou état « non compatible » ;
//  - panneau DROIT (w-[480px]) : journal d'interactions (table Seq / Dir / Type / Nom / Valeur).
// Doublé d'un guide pas-à-pas + référence des types REC_*. Actions SIMULÉES (setTimeout), aucun réseau.
// Source de vérité : automata-studio-frontend [AUT][07]-Record.js + automata_environment (InteractionType, RecordTaskService).
import React, { useState } from 'react';
import {
  PlayCircle, StopCircle, Save, RefreshCw, Wifi, WifiOff, Server, Database, FileText, User,
  Lock, ArrowUpRight, ArrowDownLeft, CheckCircle, XCircle, Info, ArrowRight, Activity, Radio,
  MousePointerClick, Folder, ChevronDown, ChevronRight, Code, AlertOctagon, Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Environnements (chacun lié à un agent) affichés dans l'arborescence — données en dur (démo).
const ENVIRONMENTS = [
  { id: 1, name: 'BGFI-Recette', agentType: 'GENERO_GDC', agentIcon: 'Settings', color: '#8B5CF6', isActive: true },
  { id: 2, name: 'Compta-Oracle', agentType: 'DATABASE_ORA', agentIcon: 'Database', color: '#F97316', isActive: true },
  { id: 3, name: 'Batch-Linux', agentType: 'OS_LINUX', agentIcon: 'Terminal', color: '#10B981', isActive: false },
];

// Fonctions disponibles par environnement (démo). type / domaine / module / programme reproduisent la carte réelle.
const FUNCTIONS = {
  1: [
    { id: 11, name: 'Ouverture de compte', type: 'PROGRAM', domain: 'CLIENT', module: 'CLIENT', program: 'p_compte', isActive: true, description: 'Saisie et validation d\'une ouverture de compte.' },
    { id: 12, name: 'Virement interne', type: 'PROGRAM', domain: 'PAIEMENT', module: 'PAIEMENT', program: 'p_virement', isActive: true, description: 'Virement entre deux comptes du même client.' },
  ],
  2: [{ id: 21, name: 'Contrôle solde', type: 'SQL', domain: 'COMPTA', module: 'COMPTA', program: 'sql_solde', isActive: true, description: 'Requête de contrôle du solde comptable.' }],
  3: [{ id: 31, name: 'Job clôture', type: 'SHELL', domain: 'BATCH', module: 'BATCH', program: 'sh_cloture', isActive: false, description: 'Script de clôture journalière.' }],
};

// Scénario d'interactions REC simulées (rejouées une à une au démarrage de l'enregistrement).
// direction OUT (REQUEST) = action envoyée à l'agent ; IN (RESPONSE) = état/écho renvoyé par l'agent.
const SCRIPT = [
  { type: 'REC_START', direction: 'RESPONSE', name: 'Démarrage capture', value: 'OK' },
  { type: 'REC_STATE', direction: 'RESPONSE', name: 'Fenêtre principale', value: 'JSON' },
  { type: 'REC_ACTION', direction: 'REQUEST', name: 'Saisie champ N° compte', value: '4012345' },
  { type: 'REC_ACTION_EXECUTED', direction: 'RESPONSE', name: 'Champ N° compte', value: '4012345' },
  { type: 'REC_ACTION', direction: 'REQUEST', name: 'Saisie champ Montant', value: '150000' },
  { type: 'REC_ACTION_EXECUTED', direction: 'RESPONSE', name: 'Champ Montant', value: '150000' },
  { type: 'REC_ACTION', direction: 'REQUEST', name: 'Action Valider', value: 'ACTION' },
  { type: 'REC_STATE', direction: 'RESPONSE', name: 'Écran de confirmation', value: 'JSON' },
  { type: 'REC_SUCCESS', direction: 'RESPONSE', name: 'Opération confirmée', value: 'OK' },
];

const EMPTY_TASK = { taskName: '', login: '', password: '', taskDescription: '', isActive: true };

// ---- Petits sous-composants de la réplique ---------------------------------
const DirBadge = ({ direction }) => direction === 'REQUEST'
  ? <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"><ArrowUpRight className="h-2.5 w-2.5" />OUT</span>
  : <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"><ArrowDownLeft className="h-2.5 w-2.5" />IN</span>;

const TypeChip = ({ type }) => {
  const cls = type.includes('ERROR')
    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
    : type.includes('SUCCESS')
      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';
  return <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>{type}</span>;
};

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const RecordPOC = () => {
  const [selectedEnvId, setSelectedEnvId] = useState(ENVIRONMENTS[0].id);
  const [expandedEnvId, setExpandedEnvId] = useState(ENVIRONMENTS[0].id);
  const [selectedFnId, setSelectedFnId] = useState(FUNCTIONS[ENVIRONMENTS[0].id][0].id);
  const [task, setTask] = useState(EMPTY_TASK);
  const [recording, setRecording] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [saved, setSaved] = useState(false);
  const [connected, setConnected] = useState(true);
  const [search, setSearch] = useState('');
  const [jsonOpen, setJsonOpen] = useState(false);

  const selectedEnv = ENVIRONMENTS.find((e) => e.id === selectedEnvId) || null;
  const envFunctions = FUNCTIONS[selectedEnvId] || [];
  const selectedFn = envFunctions.find((f) => f.id === selectedFnId) || null;
  const compatible = !!selectedEnv && selectedEnv.isActive;
  const canStart = compatible && connected && !!selectedFn && !!task.taskName && !!task.login && !recording;

  const filtered = interactions.filter((i) =>
    `${i.type} ${i.name} ${i.value}`.toLowerCase().includes(search.toLowerCase())
  );

  const setT = (k) => (e) => setTask((t) => ({ ...t, [k]: e && e.target ? e.target.value : e }));

  const toggleEnv = (id) => setExpandedEnvId((cur) => (cur === id ? null : id));

  const selectEnv = (id) => {
    setSelectedEnvId(id);
    const fns = FUNCTIONS[id] || [];
    setSelectedFnId(fns[0]?.id ?? null);
    setRecording(false); setInteractions([]); setSaved(false);
  };

  // Démarre l'enregistrement : vide le journal puis ajoute les interactions une à une (simulé).
  const startRecording = () => {
    if (!canStart) return;
    setInteractions([]); setSaved(false); setRecording(true);
    SCRIPT.forEach((step, idx) => {
      window.setTimeout(() => {
        setInteractions((prev) => [
          {
            id: `int_${Date.now()}_${idx}`,
            sequence: prev.length + 1,
            time: new Date().toLocaleTimeString('fr-FR'),
            ...step,
          },
          ...prev,
        ]);
        if (idx === SCRIPT.length - 1) setRecording(false); // l'agent signale REC_COMPLETE / REC_SUCCESS
      }, 500 * (idx + 1));
    });
  };

  const stopRecording = () => setRecording(false);
  const saveTask = () => { if (!recording && interactions.length > 0) setSaved(true); };

  return (
    <div className="space-y-8">
      {/* ===== Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Enregistrement (REC)</h1>
          <Badge color="#3B82F6">Module Environnement</Badge>
          <Badge color="#F59E0B">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Enregistrement de tâches</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== Aperçu interactif de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app : icône + titre + fil d'Ariane + actions REC + état connexion */}
        <div className="px-5 py-4 bg-gradient-to-r from-blue-500 to-indigo-700 text-white">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <PlayCircle className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-xl font-bold leading-tight">Enregistrement de Tâches</h2>
                <p className="text-sm text-blue-100">
                  {selectedFn ? `${selectedEnv?.name} / ${selectedFn.name}` : 'Sélectionnez un environnement et une fonction'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={startRecording} disabled={!canStart} className={`rounded-xl text-white ${canStart ? 'bg-emerald-400/20 hover:bg-emerald-400/30' : 'bg-white/10 text-white/40'}`}>
                <PlayCircle className="h-4 w-4" />Démarrer
              </Button>
              <Button onClick={stopRecording} disabled={!recording} className={`rounded-xl text-white ${recording ? 'bg-red-400/20 hover:bg-red-400/30' : 'bg-white/10 text-white/40'}`}>
                <StopCircle className="h-4 w-4" />Arrêter
              </Button>
              <Button onClick={saveTask} disabled={recording || interactions.length === 0 || saved} className={`rounded-xl text-white ${recording || interactions.length === 0 || saved ? 'bg-white/10 text-white/40' : 'bg-white/20 hover:bg-white/30'}`}>
                <Save className="h-4 w-4" />{saved ? 'Sauvegardée' : 'Sauvegarder'}
              </Button>
              <button
                onClick={() => setConnected((c) => !c)}
                title="Basculer l'état de connexion (démo)"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${connected ? 'bg-emerald-400/20 text-emerald-50' : 'bg-red-400/20 text-red-50'}`}>
                {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}{connected ? 'Connecté' : 'Déconnecté'}
              </button>
            </div>
          </div>
        </div>

        {/* Bandeau d'état (enregistrement / connexion) */}
        {recording && (
          <div className="px-5 py-2 bg-red-50 dark:bg-red-500/10 border-b border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
            <Radio className="h-4 w-4 animate-pulse" />Enregistrement en cours — les actions sont capturées en temps réel…
          </div>
        )}
        {!connected && (
          <div className="px-5 py-2 bg-amber-50 dark:bg-amber-500/10 border-b border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2">
            <Info className="h-4 w-4" />Connexion perdue. Une reconnexion est proposée (démo : recliquez sur l'état de connexion).
          </div>
        )}

        {/* Corps 3 colonnes : Arbre+Tâche (gauche) · Capture (centre) · Journal (droite) */}
        <div className="flex flex-col lg:flex-row">
          {/* ----- Panneau GAUCHE : arborescence + carte fonction + formulaire tâche ----- */}
          <div className="lg:w-80 shrink-0 flex flex-col border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {/* Arborescence Environnement › Fonction */}
            <div className="p-2 max-h-72 overflow-auto">
              <div className="space-y-0.5">
                {ENVIRONMENTS.map((env) => {
                  const open = expandedEnvId === env.id;
                  const fns = FUNCTIONS[env.id] || [];
                  return (
                    <div key={env.id}>
                      <div
                        onClick={() => { toggleEnv(env.id); selectEnv(env.id); }}
                        className="flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-lg text-sm hover:bg-gray-100 dark:hover:bg-gray-700/30">
                        <span className="text-gray-400">{open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</span>
                        <Server className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <span className="flex-1 truncate font-medium text-gray-800 dark:text-gray-200">{env.name}</span>
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium ${env.isActive ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
                          {env.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{env.agentType}</span>
                      </div>
                      {open && fns.map((fn) => {
                        const on = fn.id === selectedFnId && env.id === selectedEnvId;
                        return (
                          <div key={fn.id}
                            onClick={() => { setSelectedEnvId(env.id); setSelectedFnId(fn.id); }}
                            style={{ paddingLeft: '32px' }}
                            className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer rounded-lg text-[13px] transition-all ${on ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-2 border-indigo-500 text-indigo-700 dark:text-indigo-300' : 'hover:bg-gray-100 dark:hover:bg-gray-700/30 text-gray-800 dark:text-gray-200'}`}>
                            <Folder className={`h-4 w-4 ${on ? 'text-indigo-500' : 'text-gray-400'}`} />
                            <span className="truncate">{fn.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Carte de la fonction sélectionnée */}
            {selectedFn && (
              <div className="border-t border-gray-200 dark:border-gray-700 p-3">
                <div className="bg-white/80 dark:bg-gray-800/80 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="h-4 w-4 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{selectedFn.name}</span>
                    <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${selectedFn.isActive ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                      {selectedFn.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div><span className="text-gray-400">Type:</span> <span className="text-gray-700 dark:text-gray-300">{selectedFn.type}</span></div>
                    <div><span className="text-gray-400">Domaine:</span> <span className="text-gray-700 dark:text-gray-300">{selectedFn.domain}</span></div>
                    <div><span className="text-gray-400">Module:</span> <span className="text-gray-700 dark:text-gray-300">{selectedFn.module}</span></div>
                    <div><span className="text-gray-400">Programme:</span> <span className="text-gray-700 dark:text-gray-300">{selectedFn.program}</span></div>
                  </div>
                  {selectedFn.description && (
                    <p className="text-[11px] text-gray-400 mt-1.5 line-clamp-2">{selectedFn.description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Formulaire « Informations de la tâche » (en bas) */}
            <div className="border-t border-gray-200 dark:border-gray-700 p-3 space-y-3">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Informations de la tâche</h3>
              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><FileText className="h-3.5 w-3.5" />Nom de la tâche <span className="text-red-500">*</span></span>
                <input value={task.taskName} onChange={setT('taskName')} disabled={recording} placeholder="Ex : Virement interne 150k"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 disabled:opacity-60" />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><User className="h-3.5 w-3.5" />Login <span className="text-red-500">*</span></span>
                <input value={task.login} onChange={setT('login')} disabled={recording} placeholder="Identifiant applicatif"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 disabled:opacity-60" />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Mot de passe</span>
                <input type="password" value={task.password} onChange={setT('password')} disabled={recording}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 disabled:opacity-60" />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1"><Info className="h-3.5 w-3.5" />Description</span>
                <textarea value={task.taskDescription} onChange={setT('taskDescription')} disabled={recording} rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-xl focus:ring-2 focus:ring-blue-500 disabled:opacity-60" />
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <div className={`relative w-9 h-5 rounded-full transition-colors ${task.isActive ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                  onClick={() => !recording && setTask((t) => ({ ...t, isActive: !t.isActive }))}>
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${task.isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">Actif</span>
              </label>
              {!canStart && !recording && (
                <p className="text-xs text-gray-400">Requis pour démarrer : agent actif, connexion établie, fonction sélectionnée, Nom de la tâche et Login.</p>
              )}
            </div>
          </div>

          {/* ----- Panneau CENTRAL : zone de capture de l'application cible ----- */}
          <div className="flex-1 min-w-0 flex flex-col p-3 bg-gray-50 dark:bg-gray-900/40">
            {compatible ? (
              <div className="flex-1 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden flex flex-col min-h-[360px]">
                <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <span className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: selectedEnv.color }}>
                    <Icon name={selectedEnv.agentIcon} className="h-4 w-4 text-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">Capture — {selectedEnv.agentType}</p>
                    <p className="text-[11px] text-gray-400">Vue de l'application cible pilotée par l'agent</p>
                  </div>
                  <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-400"><Eye className="h-3.5 w-3.5" />{recording ? 'En direct' : 'En attente'}</span>
                </div>
                <div className="flex-1 flex items-center justify-center p-8 text-center">
                  {recording ? (
                    <div>
                      <Radio className="h-10 w-10 text-red-500 mx-auto mb-3 animate-pulse" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Capture en cours sur {selectedEnv.name}</p>
                      <p className="text-xs text-gray-400 mt-1">Réalisez vos actions ({selectedFn?.name}) : elles remontent dans le journal à droite.</p>
                    </div>
                  ) : (
                    <div>
                      <MousePointerClick className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Zone de capture</p>
                      <p className="text-xs text-gray-400 mt-1">Cliquez sur « Démarrer » pour piloter l'application cible via l'agent {selectedEnv.agentType}.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-8 min-h-[360px]">
                <div className="bg-white/80 dark:bg-gray-800/80 rounded-2xl border border-gray-200/60 dark:border-gray-700/60 shadow-sm p-8 max-w-lg text-center">
                  <AlertOctagon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-amber-700 dark:text-amber-400 mb-2">Agent non compatible</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    L'environnement sélectionné n'est pas associé à un agent actif/supporté. Sélectionnez un environnement avec un agent compatible.
                  </p>
                  <Button variant="outline" onClick={() => selectEnv(ENVIRONMENTS[0].id)} className="rounded-xl mx-auto border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400">
                    <RefreshCw className="h-4 w-4" />Rafraîchir les environnements
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* ----- Panneau DROIT : journal d'interactions ----- */}
          <div className="lg:w-[480px] shrink-0 flex flex-col border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="bg-gradient-to-r from-indigo-500 to-blue-700 px-4 py-3 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2"><Activity className="h-4 w-4" />Journal d'interactions</h3>
                <p className="text-xs text-white/70">{interactions.length} interaction(s) enregistrée(s){recording ? ' — capture en direct' : ''}</p>
              </div>
              <div className="hidden sm:block w-48"><SearchBar value={search} onChange={setSearch} placeholder="Filtrer…" /></div>
            </div>

            <div className="flex-1 max-h-[520px] overflow-auto">
              {interactions.length === 0 ? (
                <EmptyState
                  icon={MousePointerClick}
                  title="Aucune interaction capturée"
                  message="Cliquez sur « Démarrer » pour lancer l'enregistrement : les actions effectuées sur l'application cible apparaîtront ici en temps réel."
                />
              ) : filtered.length === 0 ? (
                <EmptyState title="Aucun résultat" message="Aucune interaction ne correspond au filtre." />
              ) : (
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-10">Seq</th>
                      <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-16">Dir</th>
                      <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-28">Type</th>
                      <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase">Nom</th>
                      <th className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase w-20">Valeur</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                    {filtered.map((i) => (
                      <tr key={i.id} className={`text-xs ${i.direction === 'REQUEST' ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/20`}>
                        <td className="px-2 py-1.5 text-gray-400">{i.sequence}</td>
                        <td className="px-2 py-1.5"><DirBadge direction={i.direction} /></td>
                        <td className="px-2 py-1.5"><TypeChip type={i.type} /></td>
                        <td className="px-2 py-1.5"><span className="text-gray-700 dark:text-gray-300 truncate block max-w-[140px]" title={i.name}>{i.name}</span></td>
                        <td className="px-2 py-1.5">
                          {i.direction === 'RESPONSE' && i.value === 'JSON' ? (
                            <button onClick={() => setJsonOpen(true)}
                              className="px-2 py-0.5 rounded-lg text-[10px] font-medium border border-indigo-200 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                              JSON
                            </button>
                          ) : (
                            <span className="text-gray-500 truncate block max-w-[80px]" title={i.value}>{i.value}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {saved && (
              <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-500/10 border-t border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />Tâche « {task.taskName || 'sans nom'} » sauvegardée avec {interactions.filter((i) => i.type !== 'REC_ERROR').length} interaction(s).
              </div>
            )}

            {/* Détail JSON (démo) — équivalent du dialog « État de l'application client » */}
            {jsonOpen && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-900">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-white flex items-center gap-1.5"><Code className="h-3.5 w-3.5" />État de l'application client (JSON)</span>
                  <button onClick={() => setJsonOpen(false)} className="text-[11px] text-gray-300 hover:text-white">Fermer</button>
                </div>
                <pre className="text-[11px] text-green-400 font-mono whitespace-pre-wrap max-h-40 overflow-auto">{JSON.stringify({
                  clientState: 'INTERACTIVE',
                  technicalProperties: { userInterface: { window: { name: 'wMain', form: { name: 'fSaisie' } }, focus: { nodeName: 'montant', nodeType: 'FORM_FIELD' } } },
                }, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><PlayCircle className="h-5 w-5 text-blue-500" />Démarrer un enregistrement</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Déplier un <strong>environnement</strong> avec un agent <strong>actif et compatible</strong> dans l'arborescence.</GuideStep>
              <GuideStep n={2}>Sélectionner la <strong>fonction</strong> à enregistrer.</GuideStep>
              <GuideStep n={3}>Renseigner <strong>Nom de la tâche</strong> et <strong>Login</strong> (obligatoires).</GuideStep>
              <GuideStep n={4}>Vérifier que la connexion est <strong>établie</strong>, puis cliquer sur <strong>« Démarrer »</strong>.
                {' '}<button onClick={startRecording} disabled={!canStart} className="text-blue-600 dark:text-blue-400 underline disabled:opacity-40 disabled:no-underline">Essayer</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Activity className="h-5 w-5 text-blue-500" />Capturer des actions</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Exécuter les actions métier dans la <strong>zone de capture</strong> (saisies, validations…).</GuideStep>
              <GuideStep n={2}>Chaque action remonte dans le <strong>journal</strong> : <strong>OUT</strong> = action envoyée, <strong>IN</strong> = état renvoyé par l'agent.</GuideStep>
              <GuideStep n={3}>Suivre les types <code className="text-xs">REC_*</code> et filtrer si besoin via la barre de recherche.</GuideStep>
              <GuideStep n={4}>Cliquer sur <strong>« Arrêter »</strong> une fois le scénario terminé. <button onClick={stopRecording} disabled={!recording} className="text-blue-600 dark:text-blue-400 underline disabled:opacity-40 disabled:no-underline">Arrêter</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Save className="h-5 w-5 text-blue-500" />Sauvegarder en tâche</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>L'enregistrement doit être <strong>arrêté</strong> et le journal non vide.</GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Sauvegarder »</strong> : les interactions sont persistées comme une <strong>tâche rejouable</strong>.
                {' '}<button onClick={saveTask} disabled={recording || interactions.length === 0 || saved} className="text-blue-600 dark:text-blue-400 underline disabled:opacity-40 disabled:no-underline">Sauvegarder</button></GuideStep>
              <GuideStep n={3}>Les interactions en <strong>échec</strong> (<code className="text-xs">REC_ERROR</code>) sont exclues à la sauvegarde.</GuideStep>
              <GuideStep n={4}>La tâche peut ensuite être <strong>rejouée (RUN)</strong> automatiquement.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence : types d'interactions REC_* ===== */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Info className="h-5 w-5 text-blue-500" />Types d'interactions REC_*</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Type', 'Direction', 'Signification'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {[
                  ['REC_START', 'IN', 'Démarrage de la capture confirmé par l\'agent'],
                  ['REC_STATE', 'IN', 'État courant de l\'IHM cible (fenêtre / formulaire / focus) en JSON'],
                  ['REC_ACTION', 'OUT', 'Action de l\'utilisateur envoyée à l\'agent (saisie, clic, action de dialogue)'],
                  ['REC_ACTION_EXECUTED', 'IN', 'Accusé d\'exécution de l\'action (valeur REC_EXECUTED)'],
                  ['REC_PAUSE', 'OUT', 'Mise en pause de l\'enregistrement'],
                  ['REC_RESUME', 'OUT', 'Reprise de l\'enregistrement'],
                  ['REC_STOP', 'OUT', 'Arrêt de la capture'],
                  ['REC_SUCCESS', 'IN', 'Action / scénario réalisé avec succès'],
                  ['REC_ERROR', 'IN', 'Erreur remontée par l\'agent (interaction exclue à la sauvegarde)'],
                  ['REC_COMPLETE', 'IN', 'Fin complète de la session d\'enregistrement'],
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-mono text-xs">{r[0]}</td>
                    <td className="px-4 py-2">{r[1] === 'OUT' ? <DirBadge direction="REQUEST" /> : <DirBadge direction="RESPONSE" />}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_environment / InteractionType (enum) — préfixe REC_ pour l'enregistrement.</p>
        </CardContent>
      </Card>

      {/* ===== Règles de gestion + Endpoints / WebSocket ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le démarrage exige un <strong>agent actif/compatible</strong>, une <strong>connexion WebSocket établie</strong>, une <strong>fonction</strong> sélectionnée et les champs <strong>Nom</strong> + <strong>Login</strong>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Au démarrage, le journal est <strong>réinitialisé</strong> ; chaque action est numérotée par <strong>séquence</strong> et horodatée.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />La sauvegarde n'est possible que lorsque l'enregistrement est <strong>arrêté</strong> et le journal <strong>non vide</strong>.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Les interactions en <strong>échec</strong> (REC_ERROR) ne sont <strong>pas</strong> persistées dans la tâche.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />En cas de perte de connexion, la capture s'interrompt et une <strong>reconnexion</strong> est proposée.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints &amp; WebSocket</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['WS', '#8B5CF6', '/ws/record/{clientIp} (START_CAPTURE / STOP_CAPTURE / INTERACTION)'],
              ['POST', '#3B82F6', '/api/automata/record/{clientIp}/start'],
              ['POST', '#3B82F6', '/api/automata/record/{clientIp}/interaction'],
              ['POST', '#F59E0B', '/api/automata/record/{clientIp}/stop'],
              ['POST', '#10B981', '/api/automata/record/{clientIp}/save'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all text-xs">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><RefreshCw className="h-3 w-3" />Source : automata_environment / RecordTaskService — canal temps réel WebSocket (fallback REST).</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default RecordPOC;
